import BaseModel from './BaseModel';
import {
  Publication,
  Review,
  AutoScreeningClassification,
  ReviewerScreeningVote,
  ScreeningOutcome,
  FullTextReviewVote,
  FullTextReviewOutcome,
  DataExtractionReport,
  Comment,
  Tag,
  ReferenceEventLog,
} from '@/models';
import {
  RelevanceAssessment,
  InclusionAssessment,
  AssessmentState,
  ReferenceEvent,
  DuplicateCategory,
  DuplicateState,
  ReviewStage,
  ReferenceActionState,
  ScreeningOutcomeResolution,
} from '@/enums';

export default class Reference extends BaseModel {
  constructor(data = {}) {
    super(Reference, data);
    this.id = data.id;
    this.review = new Review(data.review);
    this.publication = new Publication(data.publication);
    this.studyNumber = data.studyNumber;
    this.screeningState = data.screeningState;
    this.fullTextPdfUri = data.fullTextPdfUri;
    this.fullTextPdfAutoFetched = data.fullTextPdfAutoFetched;
    this.duplicateCategory = data.duplicateCategory;
    this.duplicateOf =
      data.duplicateOf && new Reference(data.duplicateOf);
    this.potentialDuplicateOf =
      data.potentialDuplicateOf &&
      new Reference(data.potentialDuplicateOf);
    this.unresolvedReferenceDuplicates =
      data.unresolvedReferenceDuplicates?.map(
        (reference) => new Reference(reference)
      ) ?? [];
    this.autoScreeningClassification =
      data.autoScreeningClassification &&
      new AutoScreeningClassification(
        data.autoScreeningClassification
      );
    this.reviewerScreeningVotes =
      data.reviewerScreeningVotes?.map(
        (data) => new ReviewerScreeningVote(data)
      ) ?? [];
    this.screeningOutcome =
      data.screeningOutcome &&
      new ScreeningOutcome(data.screeningOutcome);
    this.fullTextReviewState = data.fullTextReviewState;
    this.fullTextReviewVotes =
      data.fullTextReviewVotes?.map(
        (vote) => new FullTextReviewVote(vote)
      ) ?? [];
    this.fullTextReviewOutcome = new FullTextReviewOutcome(
      data.fullTextReviewOutcome ?? {}
    );
    this.dataExtractionState = data.dataExtractionState;
    this.dataExtractionReports =
      data.dataExtractionReports?.map(
        (report) => new DataExtractionReport(report)
      ) ?? [];
    this.dataExtractionOutcome = data.dataExtractionOutcome
      ? new DataExtractionReport(data.dataExtractionOutcome)
      : null;
    this.comments =
      data.comments?.map((commentData) => new Comment(commentData)) ??
      [];
    this.tags = data.tags?.map((tagData) => new Tag(tagData)) ?? [];
    this.eventLogs =
      data.eventLogs?.map(
        (eventLogData) => new ReferenceEventLog(eventLogData)
      ) ?? [];
    this.createdAt = data.createdAt
      ? new Date(data.createdAt)
      : new Date();
  }

  get displayStudyId() {
    return this.studyNumber ? `#${this.studyNumber}` : '';
  }

  static sortByTime(refA, refB, ascending = true) {
    const timeDiff =
      refA.createdAt.getTime() - refB.createdAt.getTime();
    if (timeDiff !== 0) {
      return ascending ? timeDiff : -timeDiff;
    }
    const idA = refA.id;
    const idB = refB.id;
    if (!idB) {
      return 1;
    }
    if (!idA) {
      return -1;
    }
    return ascending
      ? idB.localeCompare(idA)
      : idA.localeCompare(idB);
  }

  hasScreeningConflicts() {
    return this.screeningState === AssessmentState.CONFLICTED;
  }

  hasScreeningOutcome() {
    return !!this.screeningOutcome?.outcome;
  }

  hasScreeningConflictResolutionBy(researcher) {
    return (
      this.screeningOutcome?.resolution ===
        ScreeningOutcomeResolution.CONFLICT_RESOLUTION &&
      this.screeningOutcome?.resolvedBy.researcher.id ===
        researcher.id
    );
  }

  hasFullTextReviewConflicts() {
    return this.fullTextReviewState === AssessmentState.CONFLICTED;
  }

  hasFullTextReviewOutcome() {
    return !!this.fullTextReviewOutcome.outcome;
  }

  hasFullTextPdf() {
    return !!this.fullTextPdfUri;
  }

  hasExtractionConflicts() {
    return this.dataExtractionState === AssessmentState.CONFLICTED;
  }

  isPendingFullTextReview() {
    return this.fullTextReviewState === AssessmentState.PENDING;
  }

  isPendingExtraction() {
    return this.dataExtractionState === AssessmentState.PENDING;
  }

  isPartiallyScreened() {
    return this.screeningState === AssessmentState.PARTIAL;
  }

  isPartiallyReviewed() {
    return this.fullTextReviewState === AssessmentState.PARTIAL;
  }

  isPartiallyExtracted() {
    return this.dataExtractionState === AssessmentState.PARTIAL;
  }

  isScreened() {
    return this.screeningState === AssessmentState.COMPLETE;
  }

  isReviewed() {
    return this.fullTextReviewState === AssessmentState.COMPLETE;
  }

  isExtracted() {
    return this.dataExtractionState === AssessmentState.COMPLETE;
  }

  getScreeningVoteBy(researcher) {
    return this.reviewerScreeningVotes.find(
      (vote) => vote.reviewer?.researcher?.id === researcher?.id
    );
  }

  getResearcherVote(researcher) {
    const vote = this.getScreeningVoteBy(researcher);
    return vote ? vote.vote : RelevanceAssessment.UNSCREENED;
  }

  getFullTextReviewVoteBy(researcher) {
    return this.fullTextReviewVotes.find(
      (vote) => vote.reviewer?.researcher?.id === researcher.id
    );
  }

  getResearcherFullTextReviewVote(researcher) {
    const vote = this.getFullTextReviewVoteBy(researcher);
    return vote ? vote.vote : null;
  }

  hasReviewerScreeningVoteFrom(researcher) {
    return this.reviewerScreeningVotes.some(
      (vote) => vote.reviewer?.researcher?.id === researcher?.id
    );
  }

  hasReviewerScreeningVoteFromAny(researchers) {
    return this.reviewerScreeningVotes.some((vote) =>
      researchers.some(
        (researcher) =>
          vote.reviewer?.researcher?.id === researcher.id
      )
    );
  }

  hasFullTextReviewVoteFrom(researcher) {
    return this.fullTextReviewVotes.some(
      (vote) => vote.reviewer?.researcher?.id === researcher.id
    );
  }

  hasFullTextReviewVoteFromAny(researchers) {
    return this.fullTextReviewVotes.some((vote) =>
      researchers.some(
        (researcher) =>
          vote.reviewer?.researcher?.id === researcher.id
      )
    );
  }

  hasExtractionsFrom(researcher) {
    return this.dataExtractionReports.some(
      (report) => report.reviewer?.researcher?.id === researcher.id
    );
  }

  hasExtractionsFromAny(researchers) {
    return this.dataExtractionReports.some((report) =>
      researchers.some(
        (researcher) =>
          report.reviewer?.researcher?.id === researcher.id
      )
    );
  }

  getValidAssessmentTypes() {
    const assessmentTypes = [];
    const allowScreeningControls =
      this.screeningState === AssessmentState.PENDING ||
      this.screeningState === AssessmentState.PARTIAL ||
      this.screeningState === AssessmentState.CONFLICTED ||
      (this.screeningState === AssessmentState.COMPLETE &&
        this.fullTextReviewState === AssessmentState.PENDING);
    const allowFullTextControls =
      this.screeningState === AssessmentState.COMPLETE &&
      this.screeningOutcome.outcome !==
        RelevanceAssessment.NOT_RELEVANT;
    if (allowScreeningControls) {
      assessmentTypes.push('RelevanceAssessment');
    }
    if (allowFullTextControls) {
      assessmentTypes.push('InclusionAssessment');
    }

    return assessmentTypes;
  }

  getMostRecentAssessmentBy(researcher) {
    if (this.hasFullTextReviewOutcome()) {
      return this.fullTextReviewOutcome.outcome;
    } else if (this.hasFullTextReviewConflicts()) {
      return null;
    } else if (this.hasFullTextReviewVoteFrom(researcher)) {
      return this.getResearcherFullTextReviewVote(researcher);
    } else if (this.hasScreeningConflicts()) {
      return RelevanceAssessment.UNSCREENED;
    } else if (this.hasScreeningOutcome()) {
      return this.screeningOutcome.outcome;
    } else {
      return this.getResearcherVote(researcher);
    }
  }

  duplicateState() {
    if (this.isUserDuplicate()) {
      return DuplicateState.USER_DUPLICATE;
    }
    if (this.isAutoDuplicate()) {
      return DuplicateState.AUTO_DUPLICATE;
    }
    if (this.isPotentialDuplicate()) {
      return DuplicateState.POTENTIAL_DUPLICATE;
    }
    return DuplicateState.NOT_DUPLICATE;
  }

  duplicateStateResolvedBy() {
    const latestDuplicateEvent = this._getLatestDuplicateEvent();
    if (latestDuplicateEvent) {
      return latestDuplicateEvent.eventBy;
    }
    return null;
  }

  isDuplicate() {
    return !!this.duplicateOf;
  }

  isUserDuplicate() {
    return (
      this.isDuplicate() &&
      this.duplicateCategory === DuplicateCategory.USER
    );
  }

  isAutoDuplicate() {
    return (
      this.isDuplicate() &&
      [
        DuplicateCategory.DOI_MATCH,
        DuplicateCategory.HIGHLY_LIKELY,
      ].includes(this.duplicateCategory)
    );
  }

  isPotentialDuplicate() {
    return !!this.potentialDuplicateOf;
  }

  _getLatestDuplicateEvent() {
    return this.eventLogs.find(
      (eventLog) =>
        eventLog.event === ReferenceEvent.DUPLICATE_STATE_UPDATE
    );
  }

  hasRelevantScreeningOutcome() {
    return (
      this.screeningOutcome?.outcome === RelevanceAssessment.RELEVANT
    );
  }

  hasNotRelevantScreeningOutcome() {
    return (
      this.screeningOutcome?.outcome ===
      RelevanceAssessment.NOT_RELEVANT
    );
  }

  hasExcludeFullTextReviewOutcome() {
    return (
      this.fullTextReviewOutcome.outcome ===
      InclusionAssessment.EXCLUDE
    );
  }

  getReviewStage() {
    if (
      !this.hasScreeningOutcome() ||
      this.hasNotRelevantScreeningOutcome()
    ) {
      return ReviewStage.SCREENING;
    }
    if (
      !this.hasFullTextReviewOutcome() ||
      this.hasExcludeFullTextReviewOutcome()
    ) {
      return ReviewStage.FULL_TEXT_REVIEW;
    }
    return ReviewStage.DATA_EXTRACTION;
  }

  getReviewStageStatus() {
    const reviewStage = this.getReviewStage();
    switch (reviewStage) {
      case ReviewStage.SCREENING:
        return this.screeningState;
      case ReviewStage.FULL_TEXT_REVIEW:
        return this.fullTextReviewState;
      case ReviewStage.DATA_EXTRACTION:
        return this.dataExtractionState;
    }
  }

  getScreeningActionState(review, currentResearcher) {
    const isTrustedVoter = review.researcherIsTrustedScreeningReviewer(
      currentResearcher
    );

    if (this.screeningState === AssessmentState.PENDING) {
      if (review.screeningVotesRequired === 1) {
        return isTrustedVoter
          ? ReferenceActionState.PENDING_ACTION
          : ReferenceActionState.PENDING_ACTION_BY_OTHERS;
      }
      return ReferenceActionState.PENDING_ACTION;
    }
    if (this.isPartiallyScreened()) {
      const hasVoteFromYou = this.hasReviewerScreeningVoteFrom(
        currentResearcher
      );
      const trustedVoters = review
        .getTrustedScreeningReviewers()
        .map((member) => member.researcher);
      const hasTrustedVote = this.hasReviewerScreeningVoteFromAny(
        trustedVoters
      );
      if (hasVoteFromYou) {
        return ReferenceActionState.PENDING_ACTION_ALREADY_TAKEN;
      } else if (isTrustedVoter || hasTrustedVote) {
        return ReferenceActionState.PENDING_ACTION_BY_YOU;
      } else {
        return ReferenceActionState.PENDING_ACTION_BY_OTHERS;
      }
    }
    if (this.hasScreeningConflicts()) {
      const isTrustedConflictResolver = review.researcherIsScreeningConflictResolver(
        currentResearcher
      );
      return isTrustedConflictResolver
        ? ReferenceActionState.PENDING_CONFLICT_RESOLUTION_BY_YOU
        : ReferenceActionState.PENDING_CONFLICT_RESOLUTION_BY_OTHERS;
    }
    if (this.isScreened()) {
      const screeningOutcome = this.screeningOutcome;
      const includedOrNotSure =
        screeningOutcome.isRelevant() || screeningOutcome.isNotSure();
      if (screeningOutcome.isConsensus()) {
        return includedOrNotSure
          ? ReferenceActionState.INCLUDED_BY_CONSENSUS
          : ReferenceActionState.EXCLUDED_BY_CONSENSUS;
      }
      if (screeningOutcome.isConflictResolution()) {
        return includedOrNotSure
          ? ReferenceActionState.INCLUDED_BY_CONFLICT_RESOLUTION
          : ReferenceActionState.EXCLUDED_BY_CONFLICT_RESOLUTION;
      }
      if (screeningOutcome.isManualUpdate()) {
        return includedOrNotSure
          ? ReferenceActionState.INCLUDED_BY_OVERRIDE
          : ReferenceActionState.EXCLUDED_BY_OVERRIDE;
      }
      if (screeningOutcome.isPreScreened()) {
        return includedOrNotSure
          ? ReferenceActionState.INCLUDED_ON_UPLOAD
          : ReferenceActionState.EXCLUDED_ON_UPLOAD;
      }
      if (screeningOutcome.isSuggested()) {
        return ReferenceActionState.EXCLUDED_BY_SUGGESTION;
      }
      throw new Error(
        `Unable to map the screening outcome to a screening action ${screeningOutcome}`
      );
    }
    throw new Error(
      `Unable to map the screening state '${this.screeningState}' to a screening action`
    );
  }

  getLastArchivedScreeningOutcome() {
    const validEvents = [
      ReferenceEvent.SCREENING_OUTCOME_CREATE,
      ReferenceEvent.SCREENING_OUTCOME_REVOKE,
    ];
    const currentOutcomeId = this.screeningOutcome?.id;
    const lastRevokeLog = this.eventLogs.find(
      (eventLog) =>
        validEvents.includes(eventLog.event) &&
        currentOutcomeId !== eventLog.payload.screeningOutcome?.id &&
        !eventLog.payload.screeningOutcome?.preventRestore
    );
    return lastRevokeLog?.payload?.screeningOutcome;
  }

  getLastArchivedFullTextReviewOutcome() {
    const validEvents = [
      ReferenceEvent.FULL_TEXT_REVIEW_OUTCOME_CREATE,
      ReferenceEvent.FULL_TEXT_REVIEW_OUTCOME_REVOKE,
    ];
    const currentOutcomeId = this.fullTextReviewOutcome?.id;
    const lastRevokeLog = this.eventLogs.find(
      (eventLog) =>
        validEvents.includes(eventLog.event) &&
        currentOutcomeId !==
          eventLog.payload.fullTextReviewOutcome?.id &&
        !eventLog.payload.fullTextReviewOutcome?.preventRestore
    );
    return lastRevokeLog?.payload?.fullTextReviewOutcome;
  }

  getFullTextReviewActionState(review, currentResearcher) {
    const isTrustedVoter = review.researcherIsTrustedFullTextReviewer(
      currentResearcher
    );

    if (this.fullTextReviewState === AssessmentState.PENDING) {
      if (review.fullTextReviewVotesRequired === 1) {
        return isTrustedVoter
          ? ReferenceActionState.PENDING_ACTION
          : ReferenceActionState.PENDING_ACTION_BY_OTHERS;
      }
      return ReferenceActionState.PENDING_ACTION;
    }
    if (this.isPartiallyReviewed()) {
      const hasVoteFromYou = this.hasFullTextReviewVoteFrom(
        currentResearcher
      );
      const trustedVoters = review
        .getTrustedFullTextReviewers()
        .map((member) => member.researcher);
      const hasTrustedVote = this.hasFullTextReviewVoteFromAny(
        trustedVoters
      );
      if (hasVoteFromYou) {
        return ReferenceActionState.PENDING_ACTION_ALREADY_TAKEN;
      } else if (isTrustedVoter || hasTrustedVote) {
        return ReferenceActionState.PENDING_ACTION_BY_YOU;
      } else {
        return ReferenceActionState.PENDING_ACTION_BY_OTHERS;
      }
    }
    if (this.hasFullTextReviewConflicts()) {
      const isTrustedConflictResolver = review.researcherIsFullTextReviewConflictResolver(
        currentResearcher
      );
      return isTrustedConflictResolver
        ? ReferenceActionState.PENDING_CONFLICT_RESOLUTION_BY_YOU
        : ReferenceActionState.PENDING_CONFLICT_RESOLUTION_BY_OTHERS;
    }
    if (this.isReviewed()) {
      const fullTextReviewOutcome = this.fullTextReviewOutcome;
      const included = fullTextReviewOutcome.isIncluded();
      if (fullTextReviewOutcome.isConsensus()) {
        return included
          ? ReferenceActionState.INCLUDED_BY_CONSENSUS
          : ReferenceActionState.EXCLUDED_BY_CONSENSUS;
      }
      if (fullTextReviewOutcome.isConflictResolution()) {
        return included
          ? ReferenceActionState.INCLUDED_BY_CONFLICT_RESOLUTION
          : ReferenceActionState.EXCLUDED_BY_CONFLICT_RESOLUTION;
      }
      if (fullTextReviewOutcome.isManualUpdate()) {
        return included
          ? ReferenceActionState.INCLUDED_BY_OVERRIDE
          : ReferenceActionState.EXCLUDED_BY_OVERRIDE;
      }
      throw new Error(
        `Unable to map the full text review outcome to a screening action ${fullTextReviewOutcome}`
      );
    }
    throw new Error(
      `Unable to map the full text review state '${this.fullTextReviewState}' to a screening action`
    );
  }
}
